/* RulesEngineAdminPage — Phase C · C3a-α polish
 *
 * Read-only admin surface for the cascade rules engine:
 *   • Rule table with search + slot/domain/kind filters
 *   • Live evaluate panel: fire evaluate() with any anchor tuple
 *     and see cascade output + full trace
 *   • Rule version hash badge (surfaces cache-invalidation)
 *
 * NOT included this session (deferred to C3a-γ):
 *   • Rule editing / creation / deletion
 *   • Soft-disable toggle (`active=false`)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Play, Search, Filter, Zap, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const tokenHeader = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SLOTS = ['cascade', 'advisory', 'validation', 'comm', 'gate'];
const DOMAINS = ['data_cascade', 'roof_details', 'condition_scripts',
                 'validation', 'advisory', 'communication', 'policy'];

const SLOT_COLOURS = {
  cascade:    'bg-emerald-100 text-emerald-800 border-emerald-300',
  advisory:   'bg-amber-100 text-amber-800 border-amber-300',
  validation: 'bg-rose-100 text-rose-800 border-rose-300',
  comm:       'bg-sky-100 text-sky-800 border-sky-300',
  gate:       'bg-violet-100 text-violet-800 border-violet-300',
};

export default function RulesEngineAdminPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versionHash, setVersionHash] = useState('');
  const [q, setQ] = useState('');
  const [slotFilter, setSlotFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [expandedRuleId, setExpandedRuleId] = useState(null);

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [rulesRes, versionRes] = await Promise.all([
        fetch(`${API}/api/admin/rules-engine/rules`, { headers: tokenHeader() }),
        fetch(`${API}/api/rules-engine/version`),
      ]);
      if (!rulesRes.ok) throw new Error(`rules ${rulesRes.status}`);
      const rulesData = await rulesRes.json();
      const versionData = await versionRes.json();
      setRules(rulesData.rules || []);
      setVersionHash(versionData.rule_version_hash || '');
    } catch (e) {
      toast.error(`Failed to load rules: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  const kinds = useMemo(() => {
    return Array.from(new Set(rules.map(r => r.output_kind))).sort();
  }, [rules]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rules.filter(r => {
      if (slotFilter && r.output_slot !== slotFilter) return false;
      if (domainFilter && r.domain !== domainFilter) return false;
      if (kindFilter && r.output_kind !== kindFilter) return false;
      if (qq) {
        const hay = `${r.id} ${r.name} ${r.notes || ''} ${r.source_ref || ''}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [rules, q, slotFilter, domainFilter, kindFilter]);

  if (loading) return (
    <div className="p-8 flex items-center gap-3 text-slate-600" data-testid="rules-engine-loading">
      <Loader2 className="w-5 h-5 animate-spin" /> Loading rules engine…
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-[1400px]" data-testid="rules-engine-admin-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-emerald-600" /> Rules Engine
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Phase C · C3a cascade engine. Read-only view · edit rules by
            re-seeding from <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">services/rules_engine_seeds.py</code>.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-xs text-slate-400">rule_version_hash</span>
          <code
            data-testid="rules-engine-version-hash"
            className="text-sm font-mono bg-slate-900 text-emerald-300 px-3 py-1 rounded shadow-inner"
          >
            {versionHash || '—'}
          </code>
          <span className="text-xs text-slate-500 mt-1">
            {rules.length} rules · {filtered.length} shown
          </span>
        </div>
      </header>

      <LiveEvaluatePanel />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-1.5 text-slate-500 text-sm">
          <Filter className="w-4 h-4" /> Filter
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input
            type="text" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search rule id, name, source…"
            className="pl-9 pr-3 py-1.5 text-sm border border-slate-300 rounded-md w-64 focus:outline-none focus:border-emerald-500"
            data-testid="rules-engine-search"
          />
        </div>
        <select value={slotFilter} onChange={e => setSlotFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded-md"
          data-testid="rules-engine-slot-filter">
          <option value="">All slots</option>
          {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={domainFilter} onChange={e => setDomainFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded-md"
          data-testid="rules-engine-domain-filter">
          <option value="">All domains</option>
          {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={kindFilter} onChange={e => setKindFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-300 rounded-md"
          data-testid="rules-engine-kind-filter">
          <option value="">All kinds</option>
          {kinds.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        {(q || slotFilter || domainFilter || kindFilter) && (
          <button onClick={() => { setQ(''); setSlotFilter(''); setDomainFilter(''); setKindFilter(''); }}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="rules-engine-clear-filters">
            Clear
          </button>
        )}
      </div>

      {/* Rule table */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="rules-engine-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2 w-36">ID</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2 w-24">Slot</th>
              <th className="text-left px-3 py-2 w-40">Kind</th>
              <th className="text-right px-3 py-2 w-16">Prio</th>
              <th className="text-center px-3 py-2 w-16">Stop</th>
              <th className="text-center px-3 py-2 w-16">Active</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, idx) => (
              <React.Fragment key={r.id}>
                <tr
                  onClick={() => setExpandedRuleId(expandedRuleId === r.id ? null : r.id)}
                  className={`border-t border-slate-100 hover:bg-emerald-50/50 cursor-pointer ${
                    idx % 2 ? 'bg-slate-50/40' : ''
                  }`}
                  data-testid={`rules-engine-row-${r.id}`}
                >
                  <td className="px-3 py-2 text-slate-400">
                    {expandedRuleId === r.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded border ${SLOT_COLOURS[r.output_slot] || 'bg-slate-100'}`}>
                      {r.output_slot}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.output_kind}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">{r.priority}</td>
                  <td className="px-3 py-2 text-center text-xs">
                    {r.stop_on_match ? '⏹' : '↓'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${r.active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </td>
                </tr>
                {expandedRuleId === r.id && (
                  <tr className="bg-slate-50" data-testid={`rules-engine-detail-${r.id}`}>
                    <td colSpan={8} className="px-6 py-4 space-y-2 text-xs">
                      {r.source_ref && <div><span className="text-slate-500">Source:</span> <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{r.source_ref}</code></div>}
                      {r.notes && <div><span className="text-slate-500">Notes:</span> <span className="text-slate-700">{r.notes}</span></div>}
                      {r.anchors_required?.length > 0 && (
                        <div><span className="text-slate-500">Anchors required:</span> {r.anchors_required.map(a => <code key={a} className="ml-1 bg-white px-1.5 py-0.5 rounded border border-slate-200">{a}</code>)}</div>
                      )}
                      {r.anchors_forbidden?.length > 0 && (
                        <div><span className="text-slate-500">Anchors forbidden:</span> {r.anchors_forbidden.map(a => <code key={a} className="ml-1 bg-white px-1.5 py-0.5 rounded border border-slate-200">{a}</code>)}</div>
                      )}
                      {r.predicate && Object.keys(r.predicate).length > 0 && (
                        <div>
                          <span className="text-slate-500">Predicate:</span>
                          <pre className="mt-1 bg-white p-2 rounded border border-slate-200 text-[11px] overflow-x-auto">{JSON.stringify(r.predicate, null, 2)}</pre>
                        </div>
                      )}
                      {r.output && Object.keys(r.output).length > 0 && (
                        <div>
                          <span className="text-slate-500">Output:</span>
                          <pre className="mt-1 bg-white p-2 rounded border border-slate-200 text-[11px] overflow-x-auto">{JSON.stringify(r.output, null, 2)}</pre>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">No rules match the current filters.</div>
        )}
      </div>
    </div>
  );
}

// ── Live evaluate panel ────────────────────────────────────────────
function LiveEvaluatePanel() {
  const [anchors, setAnchors] = useState({
    roof_type: '', brand: '', profile: '',
    quote_type: '', service_option: '', material: '',
  });
  const [rowName, setRowName] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTrace, setShowTrace] = useState(false);

  async function fire() {
    setBusy(true);
    try {
      const body = {
        anchors: Object.fromEntries(Object.entries(anchors).filter(([, v]) => v !== '')),
      };
      if (rowName.trim()) body.context = { row_name: rowName.trim() };
      const res = await fetch(`${API}/api/rules-engine/evaluate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      setResult(data);
    } catch (e) {
      toast.error(`Evaluate failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  const set = k => e => setAnchors(a => ({ ...a, [k]: e.target.value }));
  const fired = result?.trace?.filter(t => t.matched) || [];

  return (
    <div className="p-4 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 rounded-lg space-y-3" data-testid="rules-engine-live-eval">
      <h2 className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
        <Play className="w-4 h-4" /> Live evaluate
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {Object.keys(anchors).map(k => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">{k}</label>
            <input
              type="text" value={anchors[k]} onChange={set(k)}
              placeholder="—"
              data-testid={`rules-engine-eval-${k}`}
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:border-emerald-500"
            />
          </div>
        ))}
        <div className="col-span-2 md:col-span-3">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">context · row_name (optional)</label>
          <input
            type="text" value={rowName} onChange={e => setRowName(e.target.value)}
            placeholder='e.g. "Aluguard Ridge Cap 3m"'
            data-testid="rules-engine-eval-row-name"
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:border-emerald-500"
          />
        </div>
      </div>
      <button
        onClick={fire} disabled={busy}
        data-testid="rules-engine-eval-fire-btn"
        className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        Fire evaluate
      </button>

      {result && (
        <div className="space-y-2 pt-2 border-t border-emerald-200">
          <div>
            <h3 className="text-xs font-semibold text-slate-700 mb-1">cascade output</h3>
            <pre data-testid="rules-engine-eval-cascade" className="bg-slate-900 text-emerald-200 text-[11px] p-3 rounded overflow-x-auto">
{JSON.stringify(result.cascade, null, 2)}
            </pre>
          </div>
          <button
            onClick={() => setShowTrace(s => !s)}
            className="text-xs text-slate-600 hover:text-slate-900 underline"
            data-testid="rules-engine-eval-toggle-trace"
          >
            {showTrace ? 'Hide' : 'Show'} trace ({fired.length}/{result.trace?.length || 0} fired)
          </button>
          {showTrace && (
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded" data-testid="rules-engine-eval-trace">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">rule_id</th>
                    <th className="text-left px-2 py-1">slot</th>
                    <th className="text-center px-2 py-1">match</th>
                    <th className="text-right px-2 py-1">µs</th>
                    <th className="text-left px-2 py-1">reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trace?.map((t, i) => (
                    <tr key={i} className={t.matched ? 'bg-emerald-50' : ''}>
                      <td className="px-2 py-0.5 font-mono">{t.rule_id}</td>
                      <td className="px-2 py-0.5">{t.slot}</td>
                      <td className="px-2 py-0.5 text-center">{t.matched ? '✓' : ''}</td>
                      <td className="px-2 py-0.5 text-right font-mono">{t.elapsed_us}</td>
                      <td className="px-2 py-0.5 text-slate-500">{t.reason || t.output_kind}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
